import { Injectable, inject } from '@angular/core';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { Capacitor, PermissionState } from '@capacitor/core';
import { Router } from '@angular/router';
import { EMPTY, from } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { RestApiService } from './rest-api.service';
import { EApiDevicePlatforms, IApiPushNotification, ECapacitorPlatforms, EPushNotificationEventTypes } from 'src/models/app.model';
import { UserDataService } from 'src/data-store/user-data.service';
import { BlogRoute, MoneyAndTransactionsRoute, ProductsRoute, UserNotificationsRoute } from 'src/models/routes.model';
import { getDevicePlatform } from 'src/util/functions';

// check @capacitor/push-notifications
enum PushPermissionStatus {
  PROMPT = 'prompt',
  PROMPT_WITH_RATIONALE = 'prompt-with-rationale',
  GRANTED = 'granted',
  DENIED = 'denied'
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationsService {
  private restApiService = inject(RestApiService);
  private loggerService = inject(LoggerService);
  private userDataService = inject(UserDataService);
  private router = inject(Router);

  private token: string | null = null;
  private alreadyCheckedPermission?: PermissionState;

  public initNotifications(): void {
    if(
      !this.userDataService.loggedUser() ||
      this.alreadyCheckedPermission === PushPermissionStatus.DENIED
    ) {
      return;
    }

    from(PushNotifications.requestPermissions())
    .pipe(
      mergeMap((result) => {
        this.alreadyCheckedPermission = result.receive;
        return result.receive === PushPermissionStatus.GRANTED
          ? from(PushNotifications.register()) // Register for push
          : EMPTY
      }),
      tap(() => {
        PushNotifications.addListener('registration', (token: Token) => {
          const platform = getDevicePlatform();

          if(platform && this.token !== token.value) {
            this.token = token.value;
            this.restApiService.sendDeviceToken({ deviceToken: token.value, platform }).subscribe({
              error: error => this.loggerService.error(error),
              next: () => this.tokenRegistered(),
            });
          } else {
            this.loggerService.logInfo('Push notifications token already registered, not sending it again.');
          }
        });

        PushNotifications.addListener('registrationError', (error: unknown) => {
          this.loggerService.error('Error on Push Notifications registration: ' + JSON.stringify(error));
        });
      })
    )
    .subscribe();
  }

  private tokenRegistered(): void {
    // prevent duplicit registration calls on Nth init
    PushNotifications.removeAllListeners().then(() => {
      PushNotifications.addListener('pushNotificationActionPerformed', (pushAction: ActionPerformed) => {
        const receivedNotification = pushAction.notification.data as IApiPushNotification;

        if(receivedNotification.notificationWebId) {
          this.restApiService.resolveNotification(receivedNotification.notificationWebId).subscribe({
            error: (error) =>
              this.loggerService.error('Failed to resolve notification ID:' + receivedNotification.notificationWebId, error),
          });
        }
        if(receivedNotification.type) {
          this.redirectUser(receivedNotification);
        }
      });
    });
  }

  private redirectUser(receivedNotification: IApiPushNotification): void {
    if (
      receivedNotification.type === EPushNotificationEventTypes.ADDED_DEPOSIT ||
      receivedNotification.type === EPushNotificationEventTypes.ADDED_DEPOSIT_LESS_THEN_CASH_THRESHOLD ||
      receivedNotification.type === EPushNotificationEventTypes.TRADING_COMPLETED ||
      receivedNotification.type === EPushNotificationEventTypes.WITHDRAWAL_EXECUTION ||
      receivedNotification.type === EPushNotificationEventTypes.WITHDRAWAL_REQUEST_ALL ||
      receivedNotification.type === EPushNotificationEventTypes.WITHDRAWAL_REQUEST_PART
    ) {
      this.router.navigate([`/${MoneyAndTransactionsRoute.MONEY_TRANSACTIONS}`]);
    } else if (receivedNotification.type === EPushNotificationEventTypes.BLOG_CONFIRMED) {
      if (receivedNotification.blogId) {
        this.router.navigate([`/${BlogRoute.BLOG}/` + receivedNotification.blogId]);
      } else {
        this.router.navigate([`/${BlogRoute.BLOG}`]);
      }
    } else if (receivedNotification.type === EPushNotificationEventTypes.PORTFOLIO_LIQUIDATED) {
      this.router.navigate([`/${UserNotificationsRoute.USER_NOTIFICATIONS}`]);
    } else if (receivedNotification.type === EPushNotificationEventTypes.PORTFOLIO_DECLINE) {
      this.router.navigate([`/${ProductsRoute.INVESTMENT_DETAIL}/` + (receivedNotification.portfolioId ?? NaN)]);
    }
  }

  public deletePushNotificationsToken(): void {
    let platform = null;
    if(Capacitor.getPlatform() === ECapacitorPlatforms.ANDROID) {
      platform = EApiDevicePlatforms.ANDROID;
    } else if (Capacitor.getPlatform() === ECapacitorPlatforms.IOS) {
      platform = EApiDevicePlatforms.IOS;
    }

    if(platform && this.token) {
      this.restApiService.deleteDeviceToken({ deviceToken: this.token, platform }).subscribe({
        error: error => this.loggerService.error(error),
        next: () => this.token = null,
      });
      PushNotifications.unregister();
      PushNotifications.removeAllListeners();
    }
  }
}
